<template>
  <div>
    <!-- Below md the rows are cards and the header, the table's only sort, is hidden -->
    <StackedTableSort
      :fields="fields"
      :sort-by="localSortBy"
      :sort-desc="localSortDesc"
      @update:sortBy="setSortBy"
      @update:sortDesc="setSortDesc"
    />

    <b-table
      v-bind="$attrs"
      :fields="tableFields"
      :table-class="tableClass"
      :responsive="scroll || true"
      :class="{ 'touch-table-scroll': scroll }"
      stacked="md"
      :sort-by="localSortBy"
      :sort-desc="localSortDesc"
      v-on="tableListeners"
      @update:sortBy="setSortBy"
      @update:sortDesc="setSortDesc"
    >
      <template
        v-for="(_, name) in $scopedSlots"
        #[name]="slotData"
      >
        <slot
          :name="name"
          v-bind="slotData"
        />
      </template>
    </b-table>
  </div>
</template>

<script>
import StackedTableSort from '@/components/common/StackedTableSort.vue'

const ACTIONS_CLASS = 'touch-table__actions'

// The table's sort events, which this component answers itself and passes on (see `setSortBy`).
const SORT_EVENTS = ['update:sortBy', 'update:sort-by', 'update:sortDesc', 'update:sort-desc']

function withActionsClass(classes) {
  if (typeof classes === 'function') {
    return (...args) => [classes(...args), ACTIONS_CLASS]
  }

  return classes ? [classes, ACTIONS_CLASS] : ACTIONS_CLASS
}

/**
 * A b-table for a list worked from a phone (docs/softphone-mobile-plan.md, P3.1 to P3.10): the
 * contract _touch-table.scss documents, held in one place. Below md each row is a card, its actions
 * across the card's full width with no label, and a "Sort by" row above the cards stands in for the
 * header they lose (StackedTableSort). Wherever a finger is the pointer every action is a 44px
 * target, and from md up on a touch screen the actions are pinned to the table's right edge.
 *
 * Everything else is b-table's own: its props and listeners pass through, and so do its slots,
 * `cell(actions)` included. `fields` are the table's, and the one keyed `actions-key` gets the touch
 * table's actions classes. A list that keeps the sort, to set it or to hold it while the table is
 * re-created, binds `sort-by` and `sort-desc` with `.sync`; without them the table keeps its own.
 */
export default {
  name: 'TouchTable',

  components: { StackedTableSort },

  inheritAttrs: false,

  props: {
    fields: {
      type: Array,
      default() {
        return []
      }
    },

    actionsKey: {
      type: String,
      default: 'actions'
    },

    // From md up the actions are pinned with a mouse too, not only on a touch screen: for a table
    // that scrolls inside its panel on a desktop, where its actions start out of view (Users).
    pinActions: {
      type: Boolean,
      default: false
    },

    // 'md': the table scrolls inside its panel only below md and on a touch screen. A desktop with a
    // mouse lays it out in full, as it did before the phone layout (Available Offers). By default it
    // scrolls inside its panel at every width.
    scroll: {
      type: String,
      default: '',
      validator: value => ['', 'md'].includes(value)
    },

    sortBy: {
      type: String,
      default: ''
    },

    sortDesc: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      localSortBy: this.sortBy,
      localSortDesc: this.sortDesc
    }
  },

  computed: {
    tableFields() {
      return this.fields.map(field => (field && field.key === this.actionsKey
        ? { ...field, thClass: withActionsClass(field.thClass), tdClass: withActionsClass(field.tdClass) }
        : field))
    },

    tableClass() {
      return ['touch-table', { 'touch-table--pin-actions': this.pinActions }]
    },

    tableListeners() {
      return Object.fromEntries(Object.entries(this.$listeners).filter(([event]) => !SORT_EVENTS.includes(event)))
    }
  },

  watch: {
    sortBy(value) {
      this.localSortBy = value
    },

    sortDesc(value) {
      this.localSortDesc = value
    }
  },

  // Each passes a change on once: b-table echoes a sort it is given back as its own update.
  methods: {
    setSortBy(value) {
      if (value !== this.localSortBy) {
        this.localSortBy = value
        this.$emit('update:sortBy', value)
      }
    },

    setSortDesc(value) {
      if (value !== this.localSortDesc) {
        this.localSortDesc = value
        this.$emit('update:sortDesc', value)
      }
    }
  }
}
</script>
